import { useState, useCallback, useRef } from 'preact/hooks';
import type { ProjectSpec, I18nStrings, ProjectSpecFeature, Attachment } from '@bitio-builder/shared';

const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface ProjectSpecPanelProps {
  projectSpec: ProjectSpec | null;
  strings: I18nStrings;
  onSendMessage: (message: string) => void;
  onSubmitSpecEarly: () => void;
  isWaitingForResponse: boolean;
  uploadedAttachments: Attachment[];
  onFileUpload: (file: File) => Promise<Attachment | null>;
  onRemoveAttachment: (attachmentId: string) => void;
}

const panelStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: 'white',
};

const panelHeaderStyle: Record<string, string | number> = {
  padding: '16px',
  borderBottom: '1px solid var(--bb-border, #E5E7EB)',
  fontWeight: 600,
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const editToggleStyle = (isActive: boolean): Record<string, string | number> => ({
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  border: 'none',
  background: isActive ? 'var(--bb-primary, #4D5D32)' : 'transparent',
  color: isActive ? 'white' : 'var(--bb-muted, #6B7280)',
  cursor: 'pointer',
  transition: 'all 150ms ease',
});

const panelContentStyle: Record<string, string | number> = {
  flex: 1,
  overflow: 'auto',
  padding: '16px',
};

const sectionStyle: Record<string, string | number> = {
  marginBottom: '16px',
};

const sectionTitleStyle: Record<string, string | number> = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--bb-muted, #6B7280)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '8px',
};

const editableValueStyle = (isEditMode: boolean): Record<string, string | number> => ({
  fontSize: '14px',
  color: 'var(--bb-foreground, #1A1C17)',
  padding: '8px 10px',
  border: isEditMode ? '1px dashed var(--bb-border, #E5E7EB)' : '1px solid transparent',
  borderRadius: '4px',
  background: isEditMode ? 'var(--bb-background, #FAFAFA)' : 'transparent',
  width: '100%',
  cursor: isEditMode ? 'text' : 'default',
  transition: 'all 150ms ease',
});

const editableValueFocusStyle: Record<string, string | number> = {
  fontSize: '14px',
  color: 'var(--bb-foreground, #1A1C17)',
  padding: '8px 10px',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '4px',
  background: 'white',
  width: '100%',
  cursor: 'text',
  transition: 'all 150ms ease',
};

const featureListStyle: Record<string, string | number> = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const featureItemStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  padding: '8px 0',
  borderBottom: '1px solid var(--bb-border, #E5E7EB)',
};

const featurePriorityStyle: Record<string, string | number> = {
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  fontSize: '11px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const featureContentStyle: Record<string, string | number> = {
  flex: 1,
};

const featureNameInputStyle: Record<string, string | number> = {
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '2px',
  padding: '4px 6px',
  border: '1px solid transparent',
  borderRadius: '4px',
  background: 'transparent',
  width: '100%',
};

const featureDescInputStyle: Record<string, string | number> = {
  fontSize: '12px',
  color: 'var(--bb-muted, #6B7280)',
  padding: '2px 6px',
  border: '1px solid transparent',
  borderRadius: '4px',
  background: 'transparent',
  width: '100%',
};

const attachmentItemStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px',
  background: 'var(--bb-background, #FAFAFA)',
  borderRadius: '4px',
  marginBottom: '8px',
};

const attachmentIconStyle: Record<string, string | number> = {
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--bb-muted, #6B7280)',
};

const attachmentInfoStyle: Record<string, string | number> = {
  flex: 1,
  minWidth: 0,
};

const attachmentNameStyle: Record<string, string | number> = {
  fontSize: '13px',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const attachmentDescStyle: Record<string, string | number> = {
  fontSize: '11px',
  color: 'var(--bb-muted, #6B7280)',
};

const removeButtonStyle: Record<string, string | number> = {
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  color: 'var(--bb-muted, #6B7280)',
  cursor: 'pointer',
  borderRadius: '4px',
  flexShrink: 0,
};

const addAttachmentButtonStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  padding: '10px',
  background: 'var(--bb-background, #FAFAFA)',
  border: '1px dashed var(--bb-border, #E5E7EB)',
  borderRadius: '6px',
  color: 'var(--bb-muted, #6B7280)',
  fontSize: '13px',
  cursor: 'pointer',
  width: '100%',
};

const emptyStateStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
  textAlign: 'center',
  color: 'var(--bb-muted, #6B7280)',
  fontSize: '14px',
};

const footerStyle: Record<string, string | number> = {
  padding: '16px',
  borderTop: '1px solid var(--bb-border, #E5E7EB)',
};

const submitButtonStyle: Record<string, string | number> = {
  width: '100%',
  padding: '12px 16px',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
};

const submitButtonDisabledStyle: Record<string, string | number> = {
  ...submitButtonStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
};

const DocIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px', flexShrink: 0 }}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px', flexShrink: 0 }}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export function ProjectSpecPanel({
  projectSpec,
  strings,
  onSendMessage,
  onSubmitSpecEarly,
  isWaitingForResponse,
  uploadedAttachments,
  onFileUpload,
  onRemoveAttachment,
}: ProjectSpecPanelProps) {
  // Edit mode toggle
  const [isEditMode, setIsEditMode] = useState(false);
  // Track which field is being edited
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasData =
    uploadedAttachments.length > 0 ||
    (projectSpec &&
      (projectSpec.projectName ||
        projectSpec.targetAudience ||
        (projectSpec.features && projectSpec.features.length > 0) ||
        projectSpec.technicalNotes ||
        projectSpec.budgetIndication ||
        projectSpec.timelineIndication));

  // Start editing a field
  const startEditing = useCallback((fieldName: string, currentValue: string) => {
    setEditingField(fieldName);
    setEditValue(currentValue);
  }, []);

  // Finish editing and send message to Claude
  const finishEditing = useCallback((fieldName: string, displayName: string) => {
    if (editingField === fieldName && editValue.trim()) {
      // Send a message to Claude about the update
      const message = `Jag vill ändra ${displayName.toLowerCase()} till: ${editValue}`;
      onSendMessage(message);
    }
    setEditingField(null);
    setEditValue('');
  }, [editingField, editValue, onSendMessage]);

  // Handle key press in edit fields
  const handleKeyDown = useCallback((e: KeyboardEvent, fieldName: string, displayName: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      finishEditing(fieldName, displayName);
    } else if (e.key === 'Escape') {
      setEditingField(null);
      setEditValue('');
    }
  }, [finishEditing]);

  // Edit a feature
  const editFeature = useCallback((feature: ProjectSpecFeature, newName: string) => {
    const message = `Jag vill ändra funktionen "${feature.name}" till: ${newName}`;
    onSendMessage(message);
  }, [onSendMessage]);

  // Handle file selection
  const handleFileSelect = useCallback(
    async (e: Event) => {
      const input = e.target as HTMLInputElement;
      const selectedFiles = input.files;

      if (!selectedFiles || selectedFiles.length === 0) return;

      setIsUploading(true);

      for (const file of Array.from(selectedFiles)) {
        // Validate file type
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          console.warn(`File type not allowed: ${file.type}`);
          continue;
        }
        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          console.warn(`File too large: ${file.name}`);
          continue;
        }

        await onFileUpload(file);
      }

      setIsUploading(false);
      input.value = '';
    },
    [onFileUpload]
  );

  if (!hasData) {
    return (
      <div style={panelStyle}>
        <div style={panelHeaderStyle}>
          <span>{strings.projectSpec.title}</span>
        </div>
        <div style={emptyStateStyle}>{strings.projectSpec.noDataYet}</div>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>{strings.projectSpec.title}</span>
        <button
          style={editToggleStyle(isEditMode)}
          onClick={() => setIsEditMode(!isEditMode)}
          title={isEditMode ? 'Avsluta redigering' : 'Redigera'}
        >
          <EditIcon />
        </button>
      </div>
      <div style={panelContentStyle}>
        {/* Project Name */}
        {projectSpec?.projectName && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>{strings.projectSpec.projectName}</div>
            {editingField === 'projectName' ? (
              <input
                type="text"
                style={editableValueFocusStyle}
                value={editValue}
                onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
                onBlur={() => finishEditing('projectName', strings.projectSpec.projectName)}
                onKeyDown={(e) => handleKeyDown(e as unknown as KeyboardEvent, 'projectName', strings.projectSpec.projectName)}
                autoFocus
              />
            ) : (
              <div
                style={editableValueStyle(isEditMode)}
                onClick={isEditMode ? () => startEditing('projectName', projectSpec.projectName || '') : undefined}
                title={isEditMode ? 'Klicka för att redigera' : undefined}
              >
                {projectSpec.projectName}
              </div>
            )}
          </div>
        )}

        {/* Target Audience */}
        {projectSpec?.targetAudience && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>{strings.projectSpec.targetAudience}</div>
            {editingField === 'targetAudience' ? (
              <input
                type="text"
                style={editableValueFocusStyle}
                value={editValue}
                onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
                onBlur={() => finishEditing('targetAudience', strings.projectSpec.targetAudience)}
                onKeyDown={(e) => handleKeyDown(e as unknown as KeyboardEvent, 'targetAudience', strings.projectSpec.targetAudience)}
                autoFocus
              />
            ) : (
              <div
                style={editableValueStyle(isEditMode)}
                onClick={isEditMode ? () => startEditing('targetAudience', projectSpec.targetAudience || '') : undefined}
                title={isEditMode ? 'Klicka för att redigera' : undefined}
              >
                {projectSpec.targetAudience}
              </div>
            )}
          </div>
        )}

        {/* Features */}
        {projectSpec?.features && projectSpec.features.length > 0 && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>{strings.projectSpec.features}</div>
            <ul style={featureListStyle}>
              {projectSpec.features
                .sort((a, b) => a.priority - b.priority)
                .map((feature, i) => (
                  <li key={i} style={featureItemStyle}>
                    <div style={featurePriorityStyle}>{feature.priority}</div>
                    <div style={featureContentStyle}>
                      {editingField === `feature-${i}` ? (
                        <input
                          type="text"
                          style={{ ...featureNameInputStyle, borderColor: 'var(--bb-border, #E5E7EB)', background: 'white' }}
                          value={editValue}
                          onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
                          onBlur={() => {
                            if (editValue.trim() && editValue !== feature.name) {
                              editFeature(feature, editValue);
                            }
                            setEditingField(null);
                            setEditValue('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (editValue.trim() && editValue !== feature.name) {
                                editFeature(feature, editValue);
                              }
                              setEditingField(null);
                              setEditValue('');
                            } else if (e.key === 'Escape') {
                              setEditingField(null);
                              setEditValue('');
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <div
                          style={{
                            ...featureNameInputStyle,
                            border: isEditMode ? '1px dashed var(--bb-border, #E5E7EB)' : '1px solid transparent',
                            background: isEditMode ? 'var(--bb-background, #FAFAFA)' : 'transparent',
                            cursor: isEditMode ? 'text' : 'default',
                          }}
                          onClick={isEditMode ? () => startEditing(`feature-${i}`, feature.name) : undefined}
                          title={isEditMode ? 'Klicka för att redigera' : undefined}
                        >
                          {feature.name}
                        </div>
                      )}
                      {feature.description && (
                        <div style={featureDescInputStyle}>{feature.description}</div>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        )}

        {/* Attachments */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>{strings.projectSpec.attachments}</div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          {/* List of uploaded attachments */}
          {uploadedAttachments.map((attachment) => (
            <div key={attachment.id} style={attachmentItemStyle}>
              <div style={attachmentIconStyle}>
                <DocIcon />
              </div>
              <div style={attachmentInfoStyle}>
                <div style={attachmentNameStyle}>{attachment.filename}</div>
                <div style={attachmentDescStyle}>
                  {attachment.mimeType?.split('/')[1]?.toUpperCase() || 'FIL'}
                </div>
              </div>
              <button
                style={removeButtonStyle}
                onClick={() => onRemoveAttachment(attachment.id)}
                title="Ta bort bilaga"
              >
                <CloseIcon />
              </button>
            </div>
          ))}

          {/* Add attachment button */}
          <button
            style={addAttachmentButtonStyle}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <PlusIcon />
            {isUploading ? 'Laddar upp...' : 'Lägg till bilaga'}
          </button>
        </div>

        {/* Technical Notes */}
        {projectSpec?.technicalNotes && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>{strings.projectSpec.technicalNotes}</div>
            {editingField === 'technicalNotes' ? (
              <textarea
                style={{ ...editableValueFocusStyle, minHeight: '60px', resize: 'vertical' }}
                value={editValue}
                onInput={(e) => setEditValue((e.target as HTMLTextAreaElement).value)}
                onBlur={() => finishEditing('technicalNotes', strings.projectSpec.technicalNotes)}
                autoFocus
              />
            ) : (
              <div
                style={editableValueStyle(isEditMode)}
                onClick={isEditMode ? () => startEditing('technicalNotes', projectSpec.technicalNotes || '') : undefined}
                title={isEditMode ? 'Klicka för att redigera' : undefined}
              >
                {projectSpec.technicalNotes}
              </div>
            )}
          </div>
        )}

        {/* Budget */}
        {projectSpec?.budgetIndication && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>{strings.projectSpec.budget}</div>
            {editingField === 'budgetIndication' ? (
              <input
                type="text"
                style={editableValueFocusStyle}
                value={editValue}
                onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
                onBlur={() => finishEditing('budgetIndication', strings.projectSpec.budget)}
                onKeyDown={(e) => handleKeyDown(e as unknown as KeyboardEvent, 'budgetIndication', strings.projectSpec.budget)}
                autoFocus
              />
            ) : (
              <div
                style={editableValueStyle(isEditMode)}
                onClick={isEditMode ? () => startEditing('budgetIndication', projectSpec.budgetIndication || '') : undefined}
                title={isEditMode ? 'Klicka för att redigera' : undefined}
              >
                {projectSpec.budgetIndication}
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        {projectSpec?.timelineIndication && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>{strings.projectSpec.timeline}</div>
            {editingField === 'timelineIndication' ? (
              <input
                type="text"
                style={editableValueFocusStyle}
                value={editValue}
                onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
                onBlur={() => finishEditing('timelineIndication', strings.projectSpec.timeline)}
                onKeyDown={(e) => handleKeyDown(e as unknown as KeyboardEvent, 'timelineIndication', strings.projectSpec.timeline)}
                autoFocus
              />
            ) : (
              <div
                style={editableValueStyle(isEditMode)}
                onClick={isEditMode ? () => startEditing('timelineIndication', projectSpec.timelineIndication || '') : undefined}
                title={isEditMode ? 'Klicka för att redigera' : undefined}
              >
                {projectSpec.timelineIndication}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submit button at the bottom */}
      <div style={footerStyle}>
        <button
          style={isWaitingForResponse ? submitButtonDisabledStyle : submitButtonStyle}
          onClick={onSubmitSpecEarly}
          disabled={isWaitingForResponse}
        >
          <SendIcon />
          {strings.specConfirmation.confirm}
        </button>
      </div>
    </div>
  );
}
